import React, { memo } from "react";
import { FaTrash, FaCheck, FaArrowsAltH } from "react-icons/fa";
import { useIsMobile } from "../../../hooks/useIsMobile";

const CartSection = ({
  cart,
  onRemoveFromCart,
  onCartQuantityChange,
  onCartNoteChange,
  onClearCart,
  onFinalizeAssignment,
  showTransferSignaturesInFooter = false,
  onTransferSignaturesClick,
}) => {
  const isMobile = useIsMobile();

  if (!cart || cart.length === 0) {
    return null;
  }

  const hasRefund = cart.some((c) => (c.quantity ?? 0) < 0);
  const hasAdd = cart.some((c) => (c.quantity ?? 0) > 0);
  const continueLabel =
    hasAdd && hasRefund
      ? "לאישור חתימה וזיכוי"
      : hasRefund
        ? "לאישור זיכוי"
        : "לאישור חתימה";

  return (
    <div className="cart-section">
      <div className="cart-header">
        <h4>עגלת פריטים ({cart.length})</h4>
        <div className="cart-actions">
          <button className="clear-cart-btn" onClick={onClearCart}>
            נקה עגלה
          </button>
        </div>
      </div>
      <div className="cart-items">
        {cart.map((item) => (
          <div key={item.id} className="cart-item">
            <div className="cart-item-info">
              <div className="cart-item-name">
                {item.item}
                {item.serial && ` (${item.serial})`}
                <span
                  className={`item-tag ${
                    item.quantity < 0 ? "return-tag" : "add-tag"
                  }`}
                >
                  {item.quantity < 0 ? "זיכוי למחסן" : "הוספה"}
                </span>
              </div>
              <div className="cart-item-meta-row">
                {item.hasSerialNumber ? (
                  <div className="cart-item-quantity">
                    כמות: {Math.abs(item.quantity)}
                  </div>
                ) : (
                  <div className="cart-item-quantity">
                    <label>כמות:</label>
                    <input
                      type="number"
                      min="0"
                      max={
                        item.originalQuantity !== undefined &&
                        item.availableQuantity !== undefined
                          ? item.quantity < 0
                            ? item.originalQuantity
                            : item.availableQuantity
                          : 999
                      }
                      value={Math.abs(item.quantity)}
                      onChange={(e) => {
                        let inputValue = parseInt(e.target.value) || 0;

                        // Validation for non-serial items with availableQuantity
                        if (
                          item.originalQuantity !== undefined &&
                          item.availableQuantity !== undefined
                        ) {
                          if (item.quantity < 0) {
                            // Return: can't return more than originalQuantity
                            if (inputValue > item.originalQuantity) {
                              inputValue = item.originalQuantity;
                              alert(
                                `לא ניתן להחזיר יותר מהכמות המוקצית. הכמות הוגבלה ל-${item.originalQuantity}`,
                              );
                            }
                          } else {
                            // Addition: can't add more than availableQuantity
                            if (inputValue > item.availableQuantity) {
                              inputValue = item.availableQuantity;
                              alert(
                                `אין מספיק פריטים זמינים במלאי. הכמות הוגבלה ל-${item.availableQuantity}`,
                              );
                            }
                          }
                        }

                        // Convert to negative for returns, positive for additions
                        const newDifference =
                          item.quantity < 0 ? -inputValue : inputValue;
                        onCartQuantityChange(item.id, newDifference);
                      }}
                      onFocus={(e) => e.target.select()}
                      className="cart-item-quantity-input"
                    />
                  </div>
                )}
                <div className="cart-item-note-row">
                  <input
                    type="text"
                    className="cart-item-note-input"
                    placeholder="ציין מיקום הפריט או הערה..."
                    value={item.note || ""}
                    onChange={(e) =>
                      onCartNoteChange &&
                      onCartNoteChange(item.id, e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
            <button
              className="remove-item-btn"
              onClick={() => onRemoveFromCart(item.id)}
            >
              <FaTrash />
            </button>
          </div>
        ))}
      </div>
      <div className="cart-footer cart-footer-with-optional-transfer">
        <div className="cart-footer-grid-spacer" aria-hidden="true" />
        <button className="finalize-btn" onClick={onFinalizeAssignment}>
          <FaCheck />
          {continueLabel}
        </button>
        <div className="cart-footer-transfer-slot">
          {showTransferSignaturesInFooter && onTransferSignaturesClick ? (
            <button
              type="button"
              className={
                isMobile
                  ? "signature-transfer-cart-footer-btn signature-transfer-cart-footer-btn--mobile-icon"
                  : "signature-transfer-cart-footer-btn"
              }
              onClick={onTransferSignaturesClick}
              aria-label="החלפת חתימות"
            >
              {isMobile ? (
                <FaArrowsAltH
                  className="signature-transfer-cart-footer-btn-icon"
                  aria-hidden
                />
              ) : (
                <span className="signature-transfer-cart-footer-btn-label">
                  החלפת חתימות
                </span>
              )}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default memo(CartSection);
